import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import "./partner.css";
import {
  faCalendar,
  faCheck,
  faCoins,
  faFileContract,
  faGavel,
  faHouse,
  faInfoCircle,
  faMoneyBill,
  faScroll,
  faSpinner,
  faStamp,
  faStar,
} from "@fortawesome/free-solid-svg-icons";

import napbLogo from "/public/napb_logo.png";
import ombudsmanLogo from "/public/the_property_ombudsman.png";
import googleLogo from "/public/google_reviews_logo.png";
import trustpilotLogo from "/public/trust_pilot_logo.png";
import { trackBuyerOfferRequest } from "../Leadform/tracking";

type Partner = {
  id: number;
  partner_type: "cash buyer" | "auction partner" | "agent partner";
  business_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  logo: string;
  trust_score: number;
  established_in: number;
  properties_bought: string;
  properties_sold: string;
  napb_registered: boolean | null;
  property_ombudsman_registered: boolean | null;
  largest_auction_platform: boolean | null;
  free_appraisals: boolean | null;
  monthly_auctions: boolean | null;
  proof_of_funds: boolean | null;
  no_contract: boolean | null;
  registered_with_tpos: boolean | null;
  solicitor_fees_covered: boolean | null;
  stars_rating: null;
  reviews_count: null;
  read_more?: string;
  pivot: {
    referred: boolean;
    position: number;
  };
};

const Partner = ({ uuid, partner }: { uuid: string; partner: Partner }) => {
  const [referred, setReferred] = useState(partner.pivot.referred);
  const [loading, setLoading] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);

  const referPartner = async (partnerId: number) => {
    // Track buyer offer request
    trackBuyerOfferRequest({
      buyerId: partner.business_name.toLowerCase().replace(/\s+/g, "_"),
      buyerPosition: partner.pivot.position,
      trustScore: partner.trust_score,
    });

    try {
      setLoading(true);
      // Replace this with your actual API call
      const response = await fetch(
        `/wp-json/wordpress-leadform/v1/refer?uuid=${uuid}&partnerId=${partnerId}`,
      );
      const data = await response.json();
      setReferred(true);
    } catch (error) {
      console.error("Error referring partner:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="Partner">
      <div className="PartnerInfo">
        <img
          src={`${import.meta.env.VITE_API_URL}/storage/${partner.logo}`}
          alt={`${partner.business_name} logo`}
          style={{ maxWidth: "200px" }}
        />
        <p>
          <FontAwesomeIcon icon={faInfoCircle} />
          Established in: {partner.established_in}
        </p>
        {partner.partner_type === "cash buyer" && (
          <p>
            <FontAwesomeIcon icon={faHouse} />
            Over {partner.properties_bought} properties bought
          </p>
        )}
        {partner.partner_type === "auction partner" && (
          <p>
            <FontAwesomeIcon icon={faHouse} />
            Over {partner.properties_sold} properties sold
          </p>
        )}
      </div>
      <div className="Verified">
        {partner.partner_type === "cash buyer" && (
          <>
            <h4>Verified</h4>
            {partner.proof_of_funds && (
              <p>
                <FontAwesomeIcon icon={faMoneyBill} />
                Proof of funds
              </p>
            )}
            {partner.no_contract && (
              <p>
                <FontAwesomeIcon icon={faScroll} />
                No contract
              </p>
            )}
            {partner.registered_with_tpos && (
              <p>
                <FontAwesomeIcon icon={faStamp} />
                Registered with TPOS
              </p>
            )}
            {partner.solicitor_fees_covered && (
              <p>
                <FontAwesomeIcon icon={faGavel} />
                Solicitor fees covered
              </p>
            )}
          </>
        )}
        {partner.partner_type === "auction partner" && (
          <>
            <h4>Features</h4>
            {partner.largest_auction_platform && (
              <p>
                <FontAwesomeIcon icon={faGavel} />
                UK's Largest Auction Platform
              </p>
            )}
            {partner.free_appraisals && (
              <p>
                <FontAwesomeIcon icon={faCoins} />
                Free Appraisals
              </p>
            )}
            {partner.monthly_auctions && (
              <p>
                <FontAwesomeIcon icon={faCalendar} />
                Monthly Auctions
              </p>
            )}
          </>
        )}
      </div>
      <div className="TrustScore">
        <h4>Trust Score</h4>
        <p className="Score">
          <span className="ScoreBadge">{partner.trust_score + " "}</span>
          Very High
        </p>
        <div className="Stars">
          {Array(5)
            .fill(0)
            .map((_, index) => {
              return (
                <FontAwesomeIcon
                  key={index}
                  icon={faStar}
                  className={partner.stars_rating > index ? "filled" : "empty"}
                />
              );
            })}
        </div>
        <p>
          Out of {partner.reviews_count} reviews <br />
          <span
            className="ReadMoreHover"
            onMouseEnter={() => setShowReadMore(true)}
            onMouseLeave={() => setShowReadMore(false)}
            onFocus={() => setShowReadMore(true)}
            onBlur={() => setShowReadMore(false)}
          >
            <a href="#" onClick={(event) => event.preventDefault()}>
              Read More
            </a>
            {partner.read_more && showReadMore && (
              <div className="ReadMoreModal" role="tooltip">
                {partner.read_more}
              </div>
            )}
          </span>
        </p>
      </div>
      <div className="NextStep">
        <h4>Next Step</h4>
        <div className="Buttons">
          {referred ? (
            <button className="Secondary">Referral Requested</button>
          ) : (
            <button
              onClick={() => referPartner(partner.id)}
              className="Primary"
            >
              {loading ? (
                <FontAwesomeIcon className="Spinner" icon={faSpinner} />
              ) : (
                <>
                  {partner.partner_type === "auction partner"
                    ? "Get a Free Appraisal"
                    : "Request Cash Offer"}
                </>
              )}
            </button>
          )}
        </div>
        <p>
          <FontAwesomeIcon icon={faCheck} />
          No Cost
        </p>
        <p>
          <FontAwesomeIcon icon={faCheck} />
          No Obligation
        </p>
        <p>
          <FontAwesomeIcon icon={faCheck} />
          Priority Service
        </p>
      </div>
      <div className="Footer">
        <div>
          <h4>Registered with</h4>
          {partner.napb_registered && (
            <img
              src={napbLogo}
              alt="NAPB Registered"
              style={{ maxWidth: "100px", marginRight: "20px" }}
            />
          )}
          {partner.property_ombudsman_registered && (
            <img
              src={ombudsmanLogo}
              alt="Property Ombudsman Registered"
              style={{ maxWidth: "100px" }}
            />
          )}
        </div>
        <div>
          <h4>Reviews from</h4>
          <img
            src={googleLogo}
            alt="Google Reviews"
            style={{ maxWidth: "100px", marginRight: "20px" }}
          />
          <img
            src={trustpilotLogo}
            alt="TrustPilot Reviews"
            style={{ maxWidth: "100px" }}
          />
        </div>
      </div>
    </div>
  );
};

export default Partner;
